import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  useTimeTilMarketClose,
  useUnderlyingMapStore,
  useUserStore,
} from '@oai-frontend-monorepo/state-management';
import {
  ensureBalance,
  prefetchMarketHours,
  prefetchUnderlyingsMap,
  prefetchUserInfo,
  queryClient,
  useSubscriptionsCustomer,
  useUserInfo,
} from '@oai-frontend-monorepo/api';
import { useEffect, useState } from 'react';
import {
  fromFormat,
  parseToken,
  shortDays,
} from '@oai-frontend-monorepo/utils';
import { DateTime } from 'luxon';
import { DateZone } from '@oai-frontend-monorepo/types';
import { setSentryUser } from '../../../libs/sentry';
import { oktaAuth } from '../../../libs/okta';
import { CircularProgress } from '@mui/material';
import ResponsiveLayout from '../../../components/layout/responsive-layout';
import { captureException } from '@sentry/react';
import { _renewToken } from '../../../libs/okta/useRenewOktaToken';
import mixpanel from 'mixpanel-browser';

interface Props {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: Props) => {
  const token = useUserStore((s) => s.token);
  const {
    data: userInfo,
    isLoading: loadingUserInfo,
    failureCount: userFailureCount,
  } = useUserInfo();

  const {
    data: subscriptionCust,
    isLoading,
    failureCount,
  } = useSubscriptionsCustomer();

  const location = useLocation();

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (token) {
      setSentryUser(token);

      const today = DateTime.utc();

      let expiryHour = 16;

      if (shortDays?.[today.toFormat('yyyy-MM-dd') as keyof typeof shortDays]) {
        expiryHour = 13;
      }

      useTimeTilMarketClose.getState().setExpiryHour(expiryHour);

      const expiry = today
        .setZone(DateZone.New_York)
        .startOf('day')
        .plus({ hour: expiryHour });

      interval = setInterval(() => {
        try {
          const diff = expiry.diff(
            DateTime.utc().setZone(DateZone.New_York),
            'seconds'
          ).seconds;
          useTimeTilMarketClose.getState().setTime(diff);
        } catch (e) {
          console.log('issue with setting time', e);
        }

        const token = useUserStore.getState().token;

        const parsed = parseToken(token);

        if (parsed && parsed.exp < Date.now() / 1000) {
          useUserStore.getState().setToken(undefined);
          queryClient.clear();
          oktaAuth.clearStorage();
          oktaAuth.closeSession();
        }
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const url = location.pathname + location.search;

      if (url.includes('cancel-subscription')) {
        mixpanel.track('Canceled Subscription');

        return navigate('/join-tradier');
      }

      if (url.includes('subscribed')) {
        setLoading(true);

        const ensureGroup = async (attempt = 1): Promise<boolean> => {
          if (attempt > 20) return false;
          await new Promise((r) => setTimeout(r, 500));

          console.log(
            'Checking subscription group membership, attempt:',
            attempt
          );

          const token = await _renewToken();
          if (
            !token?.groups?.includes('Subscribers') &&
            !token?.groups?.includes('Traders')
          ) {
            return ensureGroup(attempt + 1);
          }
          return true;
        };

        const ok = await ensureGroup();
        if (!ok) {
          captureException(
            new Error('Subscription group check failed after multiple attempts')
          );
          return alert('Subscription group check failed.');
        }

        try {
          await Promise.all([
            ensureBalance(),
            prefetchUserInfo(),
            prefetchMarketHours(),
            prefetchUnderlyingsMap({
              onSuccess: useUnderlyingMapStore.getState().setMap,
            }),
          ]);
        } catch (err) {
          console.error('Prefetch error', err);
        }

        mixpanel.track('Started Subscription');

        navigate('/dashboard');

        setLoading(false);

        return;
      }
    };

    init();
  }, [location.pathname, location.search, navigate]);

  if (
    (isLoading && failureCount < 1) ||
    (loadingUserInfo && userFailureCount < 1) ||
    loading
  ) {
    return (
      <ResponsiveLayout>
        <CircularProgress />
      </ResponsiveLayout>
    );
  }

  // need the linnked date of tradier;

  const isLegacySub =
    userInfo?.firstTradierLinkedAt &&
    DateTime.fromISO(userInfo.firstTradierLinkedAt) <
      fromFormat('2025-05-09', 'yyyy-MM-dd');

  const hasActiveSub =
    subscriptionCust?.subscriptions &&
    Array.isArray(subscriptionCust.subscriptions.data) &&
    subscriptionCust.subscriptions.data.some(
      (s) => s.status === 'active' || s.status === 'trialing'
    );

  const isAllowablePath =
    location.pathname.includes('join-tradier') ||
    location.pathname.includes('tradier/auth-callback') ||
    location.pathname.includes('subscribers') ||
    location.pathname.includes('account');

  if (!isLegacySub && !hasActiveSub && !isAllowablePath) {
    return <Navigate to="/join-tradier" />;
  }

  if (!token) {
    return <Navigate to="/" />;
  }

  return children;
};

export default ProtectedRoute;
